/**
 * useCampaignData Hook
 *
 * Custom hook for fetching and managing campaign data.
 */

import { useState, useEffect, useCallback } from 'react'
import { Campaign } from '@/types/campaigns'
import { campaignsService } from '@/services/campaigns'
import { useCampaigns } from '@/context/campaigns'

export const useCampaignData = (campaignId?: string) => {
    const { currentCampaign, setCurrentCampaign, updateCampaign, setLoadingState } = useCampaigns()
    const [error, setError] = useState<string | null>(null)

    const fetchCampaign = useCallback(
        async (id: string) => {
            setLoadingState('campaigns', true)
            setError(null)
            try {
                const campaign = await campaignsService.getCampaign(id)
                setCurrentCampaign(campaign)
                return campaign
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to fetch campaign'
                setError(message)
                return null
            } finally {
                setLoadingState('campaigns', false)
            }
        },
        [setCurrentCampaign, setLoadingState]
    )

    const refreshCampaign = useCallback(async () => {
        if (!currentCampaign?._id) return
        await fetchCampaign(currentCampaign._id)
    }, [currentCampaign?._id, fetchCampaign])

    const updateCampaignData = useCallback(
        async (id: string, updates: Partial<Campaign>) => {
            setError(null)
            try {
                const updated = await campaignsService.updateCampaign(id, updates)
                updateCampaign(id, updated)
                return updated
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to update campaign'
                setError(message)
                return null
            }
        },
        [updateCampaign]
    )

    useEffect(() => {
        if (campaignId && (!currentCampaign || currentCampaign._id !== campaignId)) {
            fetchCampaign(campaignId)
        }
    }, [campaignId, currentCampaign, fetchCampaign])

    return {
        campaign: currentCampaign,
        loading: false,
        error,
        fetchCampaign,
        refreshCampaign,
        updateCampaignData,
    }
}
